import { useEffect, useState } from 'react'
import moment from 'moment'
import { Text, Button, Separator } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { useAppDispatch } from '@/utils/typeHelpers'
import { usePrevious, useReselector } from '@/utils/sharedHooks'
import { fetchTransactionsByCategoryId } from '@/actions/admin/transactionActions'
import { fetchAdminJournalEntryLines } from '@/actions/reportActionsV2'
import { selectJournalEntryLineIdsByCategoryId } from '@/reducers/admin/journalEntryLineReducer'

import { LineItemTransaction } from './LineItemTransaction'
import { LineItemJournalEntry } from './LineItemJournalEntry'
import { getProfitAndLossTransactionsByCategoryId } from '@/features/transactions/transactions.selectors'

const LineItemExpanded = ({
  item,
  startDate,
  endDate,
  open,
  userIdForAdmin,
  confirmed,
}: {
  item: { sum: number | string; transactionCategoryId: number | null }
  startDate: string | undefined
  endDate: string | undefined
  open: boolean
  userIdForAdmin: number | null
  confirmed?: boolean
}) => {
  const dispatch = useAppDispatch()
  const [viewAllTransactions, setViewAllTransactions] = useState(false)
  const [viewedTransactionIds, setViewedTransactionIds] = useState<number[]>([])
  const [viewedJournalEntryIds, setViewedJournalEntryIds] = useState<number[]>(
    []
  )
  const [hasBeenFetched, setHasBeenFetched] = useState(false)
  const previousStartDate = usePrevious(startDate)
  const previousEndDate = usePrevious(endDate)

  useEffect(() => {
    if (previousStartDate !== startDate || endDate !== previousEndDate) {
      setHasBeenFetched(false)
    }
  }, [startDate, endDate, previousStartDate, previousEndDate])

  const transactionsByCategory = useReselector(
    getProfitAndLossTransactionsByCategoryId,
    userIdForAdmin,
    item.transactionCategoryId
  )

  const journalEntriesByCategory = useReselector(
    selectJournalEntryLineIdsByCategoryId,
    startDate,
    endDate,
    item.transactionCategoryId
  )

  useEffect(() => {
    if (open && !hasBeenFetched) {
      fetchTransactionsByCategoryId({
        id: item.transactionCategoryId,
        limit: null,
        startDate: startDate ? moment(startDate, 'MM-DD-YYYY') : undefined,
        endDate: endDate ? moment(endDate, 'MM-DD-YYYY') : undefined,
        userId: userIdForAdmin,
      })(dispatch)
      fetchAdminJournalEntryLines({
        startDate:
          moment(startDate, 'MM-DD-YYYY') ?? moment('01-01-2023', 'MM-DD-YYYY'),
        endDate:
          moment(endDate, 'MM-DD-YYYY') ?? moment('12-31-2023', 'MM-DD-YYYY'),
        userId: Number(userIdForAdmin),
        transactionCategoryId: Number(item.transactionCategoryId),
      })(dispatch)
      setHasBeenFetched(true)
    }
  }, [
    item.transactionCategoryId,
    dispatch,
    endDate,
    startDate,
    open,
    hasBeenFetched,
    userIdForAdmin,
  ])

  const numOfTransactions = transactionsByCategory.transactionIds.length

  useEffect(() => {
    if (confirmed) {
      if (!journalEntriesByCategory.length) {
        return
      }
      if (viewAllTransactions) {
        setViewedJournalEntryIds(journalEntriesByCategory)
      } else {
        setViewedJournalEntryIds(journalEntriesByCategory.slice(0, 10))
      }
    } else {
      if (!transactionsByCategory.transactionIds.length) {
        return
      }
      if (viewAllTransactions) {
        setViewedTransactionIds(transactionsByCategory.transactionIds)
      } else {
        setViewedTransactionIds(
          transactionsByCategory.transactionIds.slice(0, 10)
        )
      }
    }
  }, [
    viewAllTransactions,
    transactionsByCategory.transactionIds,
    journalEntriesByCategory,
    numOfTransactions,
    confirmed,
  ])

  if (
    !open ||
    (!viewedTransactionIds.length && !viewedJournalEntryIds.length)
  ) {
    return null
  }

  if (confirmed) {
    return (
      <div className="flex flex-col gap-4 p-2">
        <Separator />

        {viewedJournalEntryIds.map((id) => (
          <LineItemJournalEntry key={id} journalEntryId={id} />
        ))}

        <div className="flex flex-wrap items-center">
          {Boolean(numOfTransactions) && (
            <Text
              color={TextColors.MUTED}
              variant={TypographyStyles.BODY_SM}
              className="w-full md:w-1/2"
            >
              Showing {viewedJournalEntryIds.length} of&nbsp;
              {journalEntriesByCategory.length}
            </Text>
          )}
          {numOfTransactions > 10 && (
            <Button
              variant="link"
              onClick={() => setViewAllTransactions((prev) => !prev)}
              className="w-full md:w-1/2 flex justify-end"
            >
              {viewAllTransactions
                ? 'View fewer journal entries'
                : 'View all journal entries'}
            </Button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 p-2">
      <Separator color={TextColors.MUTED} />

      {viewedTransactionIds.map((id) => (
        <LineItemTransaction
          key={id}
          transactionId={id}
          userIdForAdmin={userIdForAdmin}
        />
      ))}

      <div className="flex flex-wrap items-center">
        {Boolean(numOfTransactions) && (
          <div className="w-full md:w-1/2">
            <Text color={TextColors.MUTED}>
              Showing {viewedTransactionIds.length} of&nbsp;
              {transactionsByCategory.totalCount}
            </Text>
          </div>
        )}
        {numOfTransactions > 10 && (
          <Button
            variant="link"
            onClick={() => setViewAllTransactions((prev) => !prev)}
            className="w-full md:w-1/2 flex justify-end"
          >
            {viewAllTransactions
              ? 'View fewer transactions'
              : 'View all transactions'}
          </Button>
        )}
      </div>
    </div>
  )
}

export { LineItemExpanded }
